import { Injectable } from '@angular/core';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { environment } from 'src/environments/environment';
import {
  ActionPerformed,
  PushNotificationSchema,
  PushNotifications,
  Token,
} from '@capacitor/push-notifications';
import { Platform } from '@ionic/angular';
import { NotificationsService } from './notifications.service';
import { HttpService } from './http.service';
import { UiServices } from './ui-services';
import { TranslateService } from '@ngx-translate/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationsService {
  private _token: string = '';
  public message: any = null;

  get token() {
    return this._token;
  }

  set token(value) {
    this._token = value;
  }

  constructor(
    private _platform: Platform,
    private _notificationSv: NotificationsService,
    private httpSv: HttpService,
    private uiSv: UiServices,
    private trSv: TranslateService
  ) {}

  // Helper to detect iOS browser NOT in standalone (PWA) mode
  private isIosWithNoStandalone(): boolean {
    const ua = window.navigator.userAgent;
    const isIOS = /iP(ad|hone|od)/.test(ua);
    const isInStandaloneMode =
      ('standalone' in window.navigator && window.navigator['standalone']) ||
      window.matchMedia('(display-mode: standalone)').matches;
    return isIOS && !isInStandaloneMode;
  }

  initPush() {
    if (this._platform.is('capacitor')) {
      this.pushMobile();
    } else {
      if (!this._platform.is('capacitor')) {
        this.pushWeb();
      }
    }
  }

  pushWeb() {
    if (this.isIosWithNoStandalone()) {
      // Show message to user: install app to home screen for notifications
      this.uiSv.showToast?.(
        this.trSv.instant('install_pwa_for_notifications') ||
          'To receive notifications, please install this app to your home screen.'
      );
      return;
    }
    this.requestPermission();
    this.listen();
  }

  pushMobile() {
    PushNotifications.requestPermissions().then(async (result) => {
      if (result.receive === 'granted') {
        console.log('Push Notifications permission granted:', result);
        try {
          await PushNotifications.register();
          const fcmToken = await FirebaseMessaging.getToken();
          console.log('FCM Token:', fcmToken.token);
          this.token = fcmToken.token;
          console.log('PushNotifications.register() called successfully');
        } catch (error) {
          console.error('Error during PushNotifications.register():', error);
        }
      } else {
        console.warn('Push Notifications permission not granted:', result);
        this.uiSv.showToast(
          this.trSv.instant('error_registering_push_notifications')
        );
      }
    });

    PushNotifications.addListener('registrationError', (error: any) => {
      console.error('Error on registration: ', error);
      this.uiSv.showToast(
        this.trSv.instant('error_registering_push_notifications')
      );
    });

    PushNotifications.addListener('registration', (token: Token) => {
      console.log('Push registration token received:', token);
      if (token && token.value) {
        this.token = token.value;
      } else {
        console.warn('Token is undefined or empty');
      }
    });

    PushNotifications.addListener(
      'pushNotificationReceived',
      async (notification: PushNotificationSchema) => {
        await this.showNotificationAlert();
      }
    );

    PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (notification: ActionPerformed) => {
        this.doAction(notification.notification);
      }
    );
  }

  requestPermission() {
    if (this.isIosWithNoStandalone()) {
      // Show message to user: install app to home screen for notifications
      this.uiSv.showToast?.(
        this.trSv.instant('install_pwa_for_notifications') ||
          'To receive notifications, please install this app to your home screen.'
      );
      return;
    }
    try {
      const messaging = getMessaging();
      getToken(messaging, { vapidKey: environment.firebaseConfig.vapidKey })
        .then(async (currentToken) => {
          if (currentToken) {
            this.token = currentToken;
            if (this.httpSv.token) {
              await this.httpSv.itemAction(
                {
                  tokenPush: this.token,
                },
                'auth',
                'update'
              );
            }
          } else {
            console.error(
              'No registration token available. Request permission to generate one.'
            );
          }
        })
        .catch(async (err) => {
          if (Notification.permission === 'denied') {
            const msg = this.trSv.instant(
              'notifications_are_blocked_please_enable_them_in_browser_settings'
            );
            await this.uiSv.showToast(msg);
            return;
          }

          if (
            err.code === 'messaging/permission-blocked' &&
            Notification.permission === 'default'
          ) {
            const msg = this.trSv.instant('quiere_recibir_notificaciones');
            const alert = await this.uiSv.presentAlert(msg);
            if (alert.role === 'accept') {
              this.requestPermission();
            }
          }
          console.error('An error occurred while retrieving token. ', err);
        });
    } catch (error) {
      console.error('Error in requestPermission: ', error);
    }
  }

  doAction(notification: any) {
    this._notificationSv.goToNotification(notification);
  }

  async showNotificationAlert() {
    this.soundNotification();
    await this._notificationSv.totalNotifications();
    this._notificationSv.showNotification.emit();
  }

  soundNotification() {
    const context = new AudioContext();
    const T = context.currentTime;

    const osc1 = context.createOscillator();
    osc1.type = 'square';
    osc1.frequency.value = 800;

    const osc2 = context.createOscillator();
    osc2.type = 'square';
    osc2.frequency.value = 540;

    const gain = context.createGain();
    osc1.connect(gain);
    osc2.connect(gain);
    gain.gain.setValueAtTime(0.5, T);
    gain.gain.exponentialRampToValueAtTime(0.01, T + 1.0);

    const filter = context.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 800;
    gain.connect(filter);

    filter.connect(context.destination);

    osc1.start(T);
    osc2.start(T);
    osc1.stop(T + 1.1);
    osc2.stop(T + 1.1);
  }

  listen() {
    const messaging = getMessaging();
    onMessage(messaging, (payload) => {
      console.log('payload :>> ', payload);
      this.showNotificationAlert();
    });
  }
}
